import {
  Box, Typography, Button, Container, Grid, Card, CardContent,
  Stack, Chip, AppBar, Toolbar, IconButton, useTheme,
} from '@mui/material'
import {
  DeviceHub, Speed, Map, Analytics, Notifications, Security,
  Schedule, LocalShipping, GpsFixed, Code, ArrowForward,
  Language,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useThemeMode } from '../../theme/ThemeProvider'
import { LANGUAGES, LanguageCode } from '../../i18n'
import { useState } from 'react'
import { Menu, MenuItem } from '@mui/material'

const FEATURES = [
  { icon: <GpsFixed />, title: 'Real-Time GPS Tracking', desc: 'Track vehicles and assets live on an interactive map with sub-minute updates from 50+ GPS device protocols.' },
  { icon: <LocalShipping />, title: 'Fleet Management', desc: 'Manage trips, dispatch jobs, monitor driver behavior, schedule maintenance, and track fuel consumption.' },
  { icon: <Map />, title: 'Geofencing', desc: 'Draw custom zones on the map. Get instant alerts when vehicles enter or exit geofenced areas.' },
  { icon: <Analytics />, title: 'Advanced Analytics', desc: 'Historical GPS tracks, speed charts, sensor trends, and 5-minute rollup aggregations with custom date ranges.' },
  { icon: <Notifications />, title: 'Smart Alerts & Rules', desc: 'Pre-built rules for overspeed, idle, SOS, battery, and geofence breach. Custom rule builder with scheduled notifications.' },
  { icon: <Schedule />, title: 'Automated Reports', desc: 'Fleet summary, trip reports, fuel consumption, speed violations — daily/weekly/monthly, delivered via email or download.' },
  { icon: <Speed />, title: 'Digital Twin', desc: 'Live device state with health scoring, sensor data, and real-time status updates across your entire fleet.' },
  { icon: <Security />, title: 'Multi-Tenant & Secure', desc: 'Full tenant isolation, RBAC roles (admin/user/driver/viewer), trust circles, and cross-tenant sharing.' },
  { icon: <Code />, title: 'Protocol Gateway', desc: 'Native binary protocol support for GT06, Concox, VL512, Teltonika, and more — no HTTP translation needed.' },
]


export default function LandingPage() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { language, setLanguage } = useThemeMode()
  const [langAnchor, setLangAnchor] = useState<null | HTMLElement>(null)

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#0f172a', color: '#fff' }}>
      {/* ── Navbar ──────────────────────────────────── */}
      <AppBar position="sticky" elevation={0} sx={{ bgcolor: 'rgba(15,23,42,0.95)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box component="img" src="/omnitrace_logo.png" alt="OmniTrace" sx={{ width: 36, height: 36 }} />
              <Typography variant="h5" fontWeight={800} sx={{ background: 'linear-gradient(135deg, #34A8E0, #2B8BB9)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                OmniTrace
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button sx={{ color: '#94a3b8' }} onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}>
                Features
              </Button>
              <Button sx={{ color: '#94a3b8' }} onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}>
                Contact Us
              </Button>
              <IconButton sx={{ color: '#94a3b8' }} onClick={(e) => setLangAnchor(e.currentTarget)}>
                <Language />
              </IconButton>
              <Menu anchorEl={langAnchor} open={Boolean(langAnchor)} onClose={() => setLangAnchor(null)}>
                {LANGUAGES.map((l) => (
                  <MenuItem key={l.code} selected={language === l.code}
                    onClick={() => { setLanguage(l.code as LanguageCode); setLangAnchor(null) }}>
                    {l.label}
                  </MenuItem>
                ))}
              </Menu>
              <Button variant="outlined" sx={{ borderColor: '#34A8E0', color: '#34A8E0', ml: 1 }}
                onClick={() => navigate('/auth/login')}>
                Sign In
              </Button>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* ── Hero ───────────────────────────────────── */}
      <Box sx={{ pt: { xs: 8, md: 14 }, pb: { xs: 8, md: 12 }, textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        {/* Gradient orbs */}
        <Box sx={{ position: 'absolute', top: -100, left: '20%', width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(52,168,224,0.15) 0%, transparent 70%)', filter: 'blur(60px)' }} />
        <Box sx={{ position: 'absolute', top: 50, right: '10%', width: 300, height: 300, borderRadius: '50%', background: 'radial-gradient(circle, rgba(59,130,246,0.12) 0%, transparent 70%)', filter: 'blur(60px)' }} />

        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Chip label="Fleet Tracking Platform" sx={{ mb: 3, bgcolor: 'rgba(52,168,224,0.15)', color: '#a78bfa', fontWeight: 600 }} />
          <Typography variant="h2" fontWeight={800} sx={{ mb: 3, fontSize: { xs: '2.2rem', md: '3.5rem' }, lineHeight: 1.15, letterSpacing: '-0.03em' }}>
            Track Every Vehicle.{' '}
            <Box component="span" sx={{ background: 'linear-gradient(135deg, #34A8E0, #3b82f6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              Manage Your Fleet.
            </Box>
          </Typography>
          <Typography variant="h6" sx={{ mb: 5, color: '#94a3b8', maxWidth: 600, mx: 'auto', fontWeight: 400, lineHeight: 1.6 }}>
            Enterprise-grade fleet management platform with real-time GPS tracking, smart alerts, automated reporting, and support for 50+ GPS device protocols — all in one dashboard.
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center">
            <Button variant="contained" size="large" endIcon={<ArrowForward />}
              onClick={() => navigate('/auth/login')}
              sx={{ background: 'linear-gradient(135deg, #34A8E0, #2B8BB9)', px: 4, py: 1.5, fontSize: 16, fontWeight: 700, borderRadius: 2, boxShadow: '0 8px 32px rgba(52,168,224,0.3)' }}>
              Get Started Free
            </Button>
            <Button variant="outlined" size="large"
              onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
              sx={{ borderColor: 'rgba(255,255,255,0.2)', color: '#e2e8f0', px: 4, py: 1.5, fontSize: 16, borderRadius: 2 }}>
              Learn More
            </Button>
          </Stack>

          {/* Stats */}
          <Grid container spacing={4} sx={{ mt: 8 }}>
            {[
              { value: '50+', label: 'GPS Protocols' },
              { value: '99.9%', label: 'Uptime SLA' },
              { value: '<1s', label: 'Real-time Latency' },
              { value: '3', label: 'Languages (EN/AR/KU)' },
            ].map((s, i) => (
              <Grid item xs={6} md={3} key={i}>
                <Typography variant="h3" fontWeight={800} sx={{ background: 'linear-gradient(135deg, #34A8E0, #3b82f6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  {s.value}
                </Typography>
                <Typography variant="body2" color="#64748b">{s.label}</Typography>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── Features ───────────────────────────────── */}
      <Box id="features" sx={{ py: { xs: 8, md: 12 }, bgcolor: '#111827' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Chip label="Features" sx={{ mb: 2, bgcolor: 'rgba(52,168,224,0.15)', color: '#a78bfa', fontWeight: 600 }} />
            <Typography variant="h3" fontWeight={800} sx={{ mb: 2 }}>
              Everything You Need to Run Your Fleet
            </Typography>
            <Typography variant="body1" color="#94a3b8" sx={{ maxWidth: 600, mx: 'auto' }}>
              From real-time tracking to automated reports — OmniTrace covers every aspect of modern fleet management.
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {FEATURES.map((f, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Card sx={{ bgcolor: '#1e293b', border: '1px solid rgba(255,255,255,0.06)', height: '100%', transition: 'border-color 0.3s', '&:hover': { borderColor: '#34A8E0' } }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ width: 48, height: 48, borderRadius: 2, bgcolor: 'rgba(52,168,224,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2, color: '#a78bfa' }}>
                      {f.icon}
                    </Box>
                    <Typography variant="h6" fontWeight={700} sx={{ mb: 1, color: '#f1f5f9' }}>{f.title}</Typography>
                    <Typography variant="body2" sx={{ color: '#94a3b8', lineHeight: 1.7 }}>{f.desc}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── Contact Us ─────────────────────────────── */}
      <Box id="contact" sx={{ py: { xs: 8, md: 12 }, bgcolor: '#111827' }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Chip label="Contact Us" sx={{ mb: 2, bgcolor: 'rgba(52,168,224,0.15)', color: '#a78bfa', fontWeight: 600 }} />
          <Typography variant="h3" fontWeight={800} sx={{ mb: 2 }}>
            Ready to Transform Your Fleet?
          </Typography>
          <Typography variant="body1" color="#94a3b8" sx={{ mb: 4, maxWidth: 550, mx: 'auto' }}>
            Get in touch with our team to discuss your fleet management needs, request a demo, or get started today.
          </Typography>

          <Grid container spacing={3} sx={{ mb: 5 }}>
            <Grid item xs={12} sm={4}>
              <Card sx={{ bgcolor: '#1e293b', border: '1px solid rgba(255,255,255,0.06)', p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="#94a3b8" sx={{ mb: 1 }}>Phone</Typography>
                <Typography variant="h6" fontWeight={700} sx={{ color: '#f1f5f9' }}>
                  <a href="tel:+9647800010330" style={{ color: '#a78bfa', textDecoration: 'none' }}>+964 780 001 0330</a>
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card sx={{ bgcolor: '#1e293b', border: '1px solid rgba(255,255,255,0.06)', p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="#94a3b8" sx={{ mb: 1 }}>Email</Typography>
                <Typography variant="h6" fontWeight={700}>
                  <a href="mailto:info@omnitrace.app" style={{ color: '#a78bfa', textDecoration: 'none' }}>info@omnitrace.app</a>
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card sx={{ bgcolor: '#1e293b', border: '1px solid rgba(255,255,255,0.06)', p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="#94a3b8" sx={{ mb: 1 }}>Website</Typography>
                <Typography variant="h6" fontWeight={700}>
                  <a href="https://omnitrace.app" style={{ color: '#a78bfa', textDecoration: 'none' }}>omnitrace.app</a>
                </Typography>
              </Card>
            </Grid>
          </Grid>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button variant="contained" size="large" endIcon={<ArrowForward />}
              onClick={() => navigate('/auth/login')}
              sx={{ background: 'linear-gradient(135deg, #34A8E0, #2B8BB9)', px: 5, py: 1.5, fontWeight: 700, borderRadius: 2, boxShadow: '0 8px 32px rgba(52,168,224,0.3)' }}>
              Get Started
            </Button>
            <Button variant="outlined" size="large"
              href="tel:+9647800010330"
              sx={{ borderColor: 'rgba(255,255,255,0.2)', color: '#e2e8f0', px: 5, py: 1.5, borderRadius: 2 }}>
              Call Us
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* ── Footer ─────────────────────────────────── */}
      <Box sx={{ py: 4, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box component="img" src="/omnitrace_logo.png" alt="OmniTrace" sx={{ width: 24, height: 24 }} />
              <Typography variant="body2" color="#64748b">
                OmniTrace — Fleet Tracking Platform
              </Typography>
            </Box>
            <Typography variant="caption" color="#475569">
              &copy; {new Date().getFullYear()} OmniTrace. All rights reserved.
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  )
}
